import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getSession } from '../services/authService';
import { isPlatformAdminEmail } from '../constants/platformAdmin';
import {
  type PlatformMessage,
  type PlatformUserInfo,
  type AccountPassCategory,
  ACCOUNT_PASS_CATEGORY_LABEL,
  ACCOUNT_PASS_CATEGORY_PARTS,
  listAdminMessages,
  sendMessage,
  updateMessage,
  deleteMessage,
  getMessageRecipients,
  listPlatformUsers,
} from '../services/platformMessageService';

const USER_PAGE_SIZE = 10;

interface AdminMessagesPageProps {
  workspaceId: string;
}

function isAdminSessionEmail(email: string): boolean {
  return isPlatformAdminEmail(email);
}

function formatDateShort(isoStr: string): string {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return isoStr;
  return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}.`;
}

// ─── 이용권 뱃지 ───────────────────────────────
function PassPill({ cat }: { cat: AccountPassCategory }) {
  const parts = ACCOUNT_PASS_CATEGORY_PARTS[cat];
  const label = ACCOUNT_PASS_CATEGORY_LABEL[cat];
  return (
    <span
      className={`cp-admin-users-pass-user-pass-pill cp-admin-users-pass-user-pass-pill--${cat}`}
      title={label}
    >
      {parts.tier}
      {parts.tier === 'PLUS' ? ` · ${parts.detail}` : ''}
    </span>
  );
}

// ─── 발송 목록 카드 ───────────────────────────────
function MessageCard({
  msg,
  isEditing,
  onEdit,
  onDelete,
}: {
  msg: PlatformMessage;
  isEditing: boolean;
  onEdit: (msg: PlatformMessage) => void;
  onDelete: (msg: PlatformMessage) => void;
}) {
  return (
    <div className={`cp-adm-msg-card${isEditing ? ' is-editing' : ''}`}>
      <div className="cp-adm-msg-card-top">
        <span className="cp-adm-msg-card-type-pill">
          {msg.recipient_type === 'all' ? '전체' : '개별'}
        </span>
        <span className="cp-adm-msg-card-date">{formatDateShort(msg.created_at)}</span>
      </div>
      <div className="cp-adm-msg-card-body-preview">
        {msg.body.slice(0, 100)}{msg.body.length > 100 ? '…' : ''}
      </div>
      <div className="cp-adm-msg-card-actions">
        <button
          type="button"
          className={'cp-adm-msg-card-action-btn' + (isEditing ? ' is-active' : '')}
          onClick={() => onEdit(msg)}
        >
          수정
        </button>
        <button
          type="button"
          className="cp-adm-msg-card-action-btn cp-adm-msg-card-action-btn--delete"
          onClick={() => onDelete(msg)}
        >
          삭제
        </button>
      </div>
    </div>
  );
}

// ─── 이용자 블럭 ───────────────────────────────
function UserBlock({
  user,
  isSelected,
  onToggle,
}: {
  user: PlatformUserInfo;
  isSelected: boolean;
  onToggle: (userId: string) => void;
}) {
  return (
    <div
      className={`cp-adm-msg-user-block is-selectable${isSelected ? ' is-selected' : ''}`}
      role="checkbox"
      aria-checked={isSelected}
      tabIndex={0}
      onClick={() => onToggle(user.user_id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(user.user_id); }
      }}
    >
      <span className="cp-adm-msg-user-block-check" aria-hidden>
        {isSelected ? '✓' : ''}
      </span>
      <div className="cp-adm-msg-user-block-body">
        {/* 이름+이메일 */}
        <div className="cp-adm-msg-user-block-head-row">
          <span className="cp-adm-msg-user-name">
            {(user.display_name || user.email || '—').trim()}
          </span>
          <span className="cp-adm-msg-user-email">{user.email}</span>
        </div>
        {/* 이용권 — 오른쪽 */}
        <PassPill cat={user.pass_category} />
      </div>
    </div>
  );
}

// ─── 페이지네이션 버튼 ───────────────────────────────
function Pagination({
  page,
  totalPages,
  onPage,
}: {
  page: number;
  totalPages: number;
  onPage: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  const start = Math.max(0, page - 2);
  const end = Math.min(totalPages - 1, page + 2);
  const nums: number[] = [];
  for (let i = start; i <= end; i++) nums.push(i);
  return (
    <div className="cp-adm-msg-pagination">
      <button
        type="button"
        className="cp-adm-msg-page-btn"
        onClick={() => onPage(Math.max(0, page - 1))}
        disabled={page === 0}
        aria-label="이전"
      >‹</button>
      {start > 0 && <span className="cp-adm-msg-page-ellipsis">…</span>}
      {nums.map((n) => (
        <button
          key={n}
          type="button"
          className={`cp-adm-msg-page-btn${n === page ? ' is-active' : ''}`}
          onClick={() => onPage(n)}
          aria-current={n === page ? 'page' : undefined}
        >
          {n + 1}
        </button>
      ))}
      {end < totalPages - 1 && <span className="cp-adm-msg-page-ellipsis">…</span>}
      <button
        type="button"
        className="cp-adm-msg-page-btn"
        onClick={() => onPage(Math.min(totalPages - 1, page + 1))}
        disabled={page >= totalPages - 1}
        aria-label="다음"
      >›</button>
    </div>
  );
}

// ─── 메인 페이지 ───────────────────────────────
const AdminMessagesPage: React.FC<AdminMessagesPageProps> = ({ workspaceId: _workspaceId }) => {
  const [sessionEmail, setSessionEmail] = useState('');

  // 발송 목록
  const [list, setList] = useState<PlatformMessage[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // 이용자 목록
  const [users, setUsers] = useState<PlatformUserInfo[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [userPage, setUserPage] = useState(0);

  // 선택 — 항상 선택 모드, 모든 페이지 통합 관리
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  // 발송 폼 (제목 없음 — body만)
  const [draftBody, setDraftBody] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);

  const isAdmin = useMemo(() => isAdminSessionEmail(sessionEmail), [sessionEmail]);

  // 세션
  useEffect(() => {
    let cancelled = false;
    void getSession()
      .then((s) => { if (!cancelled) setSessionEmail((s?.user?.email ?? '').trim().toLowerCase()); })
      .catch(() => { if (!cancelled) setSessionEmail(''); });
    return () => { cancelled = true; };
  }, []);

  // 발송 목록 조회
  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try { setList(await listAdminMessages()); }
    catch (e) { setListError(e instanceof Error ? e.message : '조회 실패'); }
    finally { setListLoading(false); }
  }, []);

  // 이용자 목록 조회
  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try { setUsers(await listPlatformUsers()); }
    catch { /* 오류 무시 */ }
    finally { setUsersLoading(false); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (isAdmin) loadUsers(); }, [isAdmin, loadUsers]);

  // 이용자 페이지네이션
  const totalUserPages = Math.ceil(users.length / USER_PAGE_SIZE);
  const pageUsers = users.slice(userPage * USER_PAGE_SIZE, (userPage + 1) * USER_PAGE_SIZE);

  const handleToggleUser = useCallback((userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(Array.from(prev));
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  // 전체선택/해제 — 모든 페이지 대상
  const handleSelectAll = useCallback(() => {
    setSelectedUserIds(new Set(users.map((u) => u.user_id)));
  }, [users]);

  const handleClearSelection = useCallback(() => {
    setSelectedUserIds(new Set());
  }, []);

  const resetCompose = useCallback(() => {
    setEditingMessageId(null);
    setDraftBody('');
    setSelectedUserIds(new Set());
    setSendError(null);
  }, []);

  // 수정 — 본문·수신자를 발송 섹션에 로드
  const handleEdit = useCallback(async (msg: PlatformMessage) => {
    setEditingMessageId(msg.id);
    setDraftBody(msg.body);
    setSendError(null);
    setSendSuccess(null);
    try {
      if (msg.recipient_type === 'all') {
        // 구 전체발송: 현재 전체 이용자 선택으로 표시
        setSelectedUserIds(new Set(users.map((u) => u.user_id)));
      } else {
        const recipients = await getMessageRecipients(msg.id);
        setSelectedUserIds(new Set(recipients.map((r) => r.user_id)));
      }
    } catch (e) {
      setSendError(e instanceof Error ? e.message : '수신자 불러오기 실패');
    }
  }, [users]);

  const handleDelete = useCallback(async (msg: PlatformMessage) => {
    if (!window.confirm('이 메시지를 영구 삭제할까요?')) return;
    try {
      await deleteMessage(msg.id);
      setList((prev) => prev.filter((m) => m.id !== msg.id));
      if (editingMessageId === msg.id) resetCompose();
    } catch (e) {
      alert(`삭제 실패: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [editingMessageId, resetCompose]);

  const canSend = selectedUserIds.size > 0 && draftBody.trim().length > 0 && !sending;
  const isEditing = editingMessageId != null;

  // 발송 / 수정 저장
  const handleSend = useCallback(async () => {
    if (!draftBody.trim()) { setSendError('메시지 내용을 입력하세요.'); return; }
    if (selectedUserIds.size === 0) {
      setSendError('발송 대상 이용자를 1명 이상 선택하세요.');
      return;
    }
    const editing = editingMessageId != null;
    setSending(true);
    setSendError(null);
    setSendSuccess(null);
    try {
      const recipientUserIds = Array.from(selectedUserIds);
      if (editingMessageId) {
        const updated = await updateMessage(editingMessageId, {
          body: draftBody,
          recipientUserIds,
        });
        setList((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
        resetCompose();
        setSendSuccess('수정되었습니다!');
      } else {
        const msg = await sendMessage({
          body: draftBody,
          recipientType: 'individual',
          recipientUserIds,
        });
        setList((prev) => [msg, ...prev]);
        resetCompose();
        setSendSuccess('발송되었습니다!');
      }
      setTimeout(() => setSendSuccess(null), 3000);
    } catch (e) {
      setSendError(e instanceof Error ? e.message : editing ? '수정 실패' : '발송 실패');
    } finally {
      setSending(false);
    }
  }, [draftBody, selectedUserIds, editingMessageId, resetCompose]);

  // 수신 대상 요약
  const recipientSummary = useMemo(() => {
    if (selectedUserIds.size === 0) return '수신 대상 없음';
    if (selectedUserIds.size === users.length && users.length > 0) {
      return `전체 이용자 ${users.length}명`;
    }
    return `${selectedUserIds.size}명 선택됨`;
  }, [selectedUserIds, users.length]);

  if (!isAdmin && sessionEmail) {
    return (
      <section className="cp-adm-msg-page">
        <p className="cp-adm-msg-no-access">관리자 권한이 필요합니다.</p>
      </section>
    );
  }

  return (
    <section className="cp-adm-msg-page" aria-labelledby="cp-adm-msg-title">
      <div className="cp-adm-msg-page-title-row">
        <h2 id="cp-adm-msg-title">메세지 발송 관리</h2>
        <p className="cp-adm-msg-page-desc">
          플랫폼 전역 메시지. 로그인한 이용자의 메시지 수신함으로 발송됩니다.
        </p>
      </div>

      <div className="cp-adm-msg-grid">
        {/* ─── 섹션 1: 메세지 발송 목록 ─── */}
        <article className="cp-dashboard-block cp-adm-msg-section" aria-labelledby="cp-adm-msg-list-head">
          <h3 className="cp-dashboard-block__head" id="cp-adm-msg-list-head">
            <span className="cp-dashboard-block__head-title">메세지 발송 목록</span>
          </h3>
          <div className="cp-dashboard-block__body cp-adm-msg-section-body">
            {listLoading && <p className="cp-adm-msg-placeholder">불러오는 중...</p>}
            {!listLoading && listError && <p className="cp-adm-msg-error">{listError}</p>}
            {!listLoading && !listError && list.length === 0 && (
              <p className="cp-adm-msg-placeholder">발송한 메시지가 없습니다.</p>
            )}
            {!listLoading && !listError && list.length > 0 && (
              <ul className="cp-adm-msg-card-list">
                {list.map((msg) => (
                  <li key={msg.id}>
                    <MessageCard
                      msg={msg}
                      isEditing={editingMessageId === msg.id}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </article>

        {/* ─── 섹션 2: 발송 대상 선택 ─── */}
        <article className="cp-dashboard-block cp-adm-msg-section" aria-labelledby="cp-adm-msg-recipients-head">
          <h3 className="cp-dashboard-block__head" id="cp-adm-msg-recipients-head">
            <span className="cp-dashboard-block__head-title">
              발송 대상 선택
              {selectedUserIds.size > 0 && (
                <span className="cp-adm-msg-selected-count-badge">{selectedUserIds.size}</span>
              )}
            </span>
            <div className="cp-adm-msg-recipients-head-actions">
              <button type="button" className="cp-adm-msg-text-btn" onClick={handleSelectAll}>
                전체선택
              </button>
              <button type="button" className="cp-adm-msg-text-btn" onClick={handleClearSelection}>
                선택해제
              </button>
            </div>
          </h3>
          <div className="cp-dashboard-block__body cp-adm-msg-section-body cp-adm-msg-recipients-body">
            {usersLoading && <p className="cp-adm-msg-placeholder">이용자 목록 불러오는 중...</p>}
            {!usersLoading && users.length === 0 && (
              <p className="cp-adm-msg-placeholder">등록된 이용자가 없습니다.</p>
            )}
            {!usersLoading && users.length > 0 && (
              <>
                <Pagination page={userPage} totalPages={totalUserPages} onPage={setUserPage} />
                <ul className="cp-adm-msg-user-list">
                  {pageUsers.map((user) => (
                    <li key={user.user_id}>
                      <UserBlock
                        user={user}
                        isSelected={selectedUserIds.has(user.user_id)}
                        onToggle={handleToggleUser}
                      />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </article>

        {/* ─── 섹션 3: 메세지 발송 / 수정 ─── */}
        <article className="cp-dashboard-block cp-adm-msg-section" aria-labelledby="cp-adm-msg-compose-head">
          <h3 className="cp-dashboard-block__head" id="cp-adm-msg-compose-head">
            <span className="cp-dashboard-block__head-title">
              {isEditing ? '메세지 수정' : '메세지 발송'}
            </span>
            {isEditing && (
              <button type="button" className="cp-adm-msg-text-btn" onClick={resetCompose}>
                수정 취소
              </button>
            )}
          </h3>
          <div className="cp-dashboard-block__body cp-adm-msg-section-body cp-adm-msg-compose-body">
            {/* 수신 대상 요약 */}
            <div className="cp-adm-msg-recipient-summary">
              <span className="cp-adm-msg-recipient-summary-label">수신 대상</span>
              <span
                className={
                  'cp-adm-msg-recipient-summary-value' +
                  (selectedUserIds.size > 0 ? ' is-individual' : ' is-empty')
                }
              >
                {recipientSummary}
              </span>
            </div>

            {/* 내용 (제목 없음 — 본문만) */}
            <div className="cp-adm-msg-field cp-adm-msg-field--body">
              <label className="cp-adm-msg-field-label" htmlFor="cp-adm-msg-body-input">
                메시지 내용
              </label>
              <textarea
                id="cp-adm-msg-body-input"
                className="cp-adm-msg-body-textarea"
                value={draftBody}
                onChange={(e) => setDraftBody(e.target.value)}
                maxLength={5000}
                placeholder="메시지 내용을 입력하세요."
                disabled={sending}
                rows={8}
              />
            </div>

            {sendError && <p className="cp-adm-msg-send-error">{sendError}</p>}
            {sendSuccess && (
              <p className="cp-adm-msg-send-success">{sendSuccess}</p>
            )}

            <div className="cp-adm-msg-compose-actions">
              <button
                type="button"
                className="cp-adm-msg-send-btn"
                onClick={handleSend}
                disabled={!canSend}
                title={
                  selectedUserIds.size === 0
                    ? '발송 대상을 선택하세요'
                    : !draftBody.trim()
                      ? '메시지 내용을 입력하세요'
                      : undefined
                }
              >
                {sending
                  ? isEditing
                    ? '저장 중...'
                    : '발송 중...'
                  : isEditing
                    ? '저장'
                    : '발송'}
              </button>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
};

export default AdminMessagesPage;
